import type { LucideIcon } from 'lucide-react'
import {ArrowDownRight, ArrowUpRight} from 'lucide-react'

interface StatCardProps {
  label: string
  value: number
  icon: LucideIcon
  accent: 'blue' | 'orange' | 'green' | 'gray' | 'red' | 'yellow'
  trend?: number
}

const accentStyles: Record<StatCardProps['accent'], { bg: string, text: string, bar: string }> = {
  blue: { bg: 'bg-[#EEF3FE]', text: 'text-[#1E4FBF]', bar: 'bg-[#1E4FBF]' },
  orange: { bg: 'bg-[#FFF1E8]', text: 'text-[#D9622B]', bar: 'bg-[#D9622B]' },
  green: { bg: 'bg-[#EAF7EE]', text: 'text-[#1E8A45]', bar: 'bg-[#1E8A45]' },
  gray: { bg: 'bg-[#F1F2F4]', text: 'text-[#4B5563]', bar: 'bg-[#4B5563]' },
  red: { bg: 'bg-[#FEF2F2]', text: 'text-[#B91C1C]', bar: 'bg-[#B91C1C]' },
  yellow: { bg: 'bg-[#FEF9E7]', text: 'text-[#B7791F]', bar: 'bg-[#B7791F]' },
}

function StatCard({ label, value, icon: Icon, accent, trend }: StatCardProps) {
  const style = accentStyles[accent]
  return (
    <div className="relative bg-white rounded-xl sm:rounded-2xl border border-[#E7E9EC] p-3.5 sm:p-5 flex flex-col gap-2.5 sm:gap-3 shadow-[0_1px_2px_rgba(16,24,40,0.03)] hover:shadow-[0_4px_12px_rgba(16,24,40,0.06)] hover:-translate-y-0.5 transition-all duration-200 overflow-hidden">
      <div className={`absolute top-0 left-0 h-[3px] w-full ${style.bar} opacity-70`} />
      <div className="flex items-center justify-between">
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center shrink-0 ${style.bg}`}>
          <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${style.text}`} strokeWidth={1.75} />
        </div>
        {typeof trend === 'number' && (
          <span className={`flex items-center gap-0.5 text-[11px] font-medium px-1.5 py-0.5 rounded-md ${trend >= 0 ? 'text-[#1E8A45] bg-[#EAF7EE]' : 'text-[#B91C1C] bg-[#FEF2F2]'}`}>
            {trend >= 0 ? <ArrowUpRight className="w-3 h-3" strokeWidth={2} /> : <ArrowDownRight className="w-3 h-3" strokeWidth={2} />}
            {Math.abs(trend)}
            %
          </span>
        )}
      </div>
      <div className="min-w-0">
        <p className="text-[21px] sm:text-[26px] font-semibold text-[#12141A] leading-tight tabular-nums">{value}</p>
        <p className="text-[11.5px] sm:text-[12.5px] text-[#6B7280] leading-snug sm:truncate mt-0.5">{label}</p>
      </div>
    </div>
  )
}

export default StatCard
